import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../core/stores/authStore'
import { authService } from '../../core/services'
import { useTranslation } from '../../core/hooks/useTranslation'
import { createLoginSchema, type LoginForm } from '../../core/schemas'
import { UserRole } from '../../core/enums'
import { AppRoute } from '../../core/config/app-routes'
import { canAccessRoute, getDefaultRouteForRole } from '../../core/config/route-permissions'
import PhoneNumberInput from '../../components/ui/PhoneNumberInput'
import Footer from '../../components/layout/Footer'
import Header from '../../components/layout/Header'

// Test users data from seed.sql
const TEST_USERS = {
  business_owners: [
    { name: 'أحمد محمد', phone: '01234567890', email: 'ahmed@example.com' },
    { name: 'فاطمة علي', phone: '01234567891', email: 'fatma@example.com' },
    { name: 'محمد حسن', phone: '01234567892', email: 'mohamed@example.com' },
    { name: 'سارة أحمد', phone: '01234567893', email: 'sara@example.com' },
  ],
  admins: [
    { name: 'Admin', phone: '01000000000', email: 'admin@digitalmenu.com' },
    { name: 'Support', phone: '01000000001', email: 'support@digitalmenu.com' },
  ]
}

const TEST_PASSWORD = 'password123'

export default function LoginPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [showQuickLogin, setShowQuickLogin] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { setAuth } = useAuthStore()
  const { t, isRTL } = useTranslation()

  const from = location.state?.from?.pathname

  const loginSchema = createLoginSchema()

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  })


  /**
   * Get redirect path after login
   * Priority: from (if accessible) > role-based default
   */
  const getRedirectPath = (userRole: UserRole): string => {
    if (from && from !== AppRoute.LOGIN_ROUTE && from !== AppRoute.REGISTER_ROUTE) {
      if (canAccessRoute(from, userRole)) {
        return from
      }
    }
    
    return getDefaultRouteForRole(userRole)
  }

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true)
    try {
      const authResult = await authService.login(data.phoneNumber, data.password)
      
      const { user, token, refreshToken } = authResult
      
      setAuth(user, token)
      
      // Store refresh token if provided
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken)
      }
      
      toast.success(t('loginSuccess'))

      const redirectPath = getRedirectPath(user.userRole as UserRole);
      navigate(redirectPath, { replace: true });

    } catch (error: any) {
      console.error('Login error:', error)
      // Error message is already displayed by axios interceptor
      // Only show fallback if no error response was received
      if (!error?.response) {
        toast.error(error.message || t('loginFailed'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleQuickLogin = async (_userType: UserRole, phone: string) => {
    setIsLoading(true)
    try {
      const authResult = await authService.login(phone, TEST_PASSWORD)
      const { user, token, refreshToken } = authResult
      
      setAuth(user, token)
      
      // Store refresh token if provided
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken)
      }
      
      toast.success(`${t('loginQuickLoginSuccess')}`)

      const redirectPath = getRedirectPath(user.userRole as UserRole);

      console.log('redirectPath', redirectPath)

      navigate(redirectPath, { replace: true });
    } catch (error: any) {
      console.error('Quick login error:', error)
      // Error message is already displayed by axios interceptor
      // Only show fallback if no error response was received
      if (!error?.response) {
        toast.error(t('loginQuickLoginFailed'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation Bar */}
      <Header showRegisterButton headerIsSticky={false} />

      {/* Main Content - Centered Layout with Image Background */}
      <div className="min-h-[calc(100vh-73px)] flex items-center justify-center relative overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="/assets/images/background_overlay.webp"
            alt="Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#1E293B]/95 via-[#1E293B]/90 to-[#1E293B]/95"></div>
        </div>

        {/* Content */}
        <div className="relative z-20 w-full max-w-md mx-auto px-6 py-12">
          {/* Welcome Section */}
          <div className="text-center mb-6">
            <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">
              {t('loginWelcomeBackTo')}{' '}
              <span className="text-primary-600">{t('siteName')}</span>
            </h1>
            <p className="text-sm lg:text-base text-white/80 leading-relaxed">
              {t('siteDescription')}
            </p>
          </div>

          {/* Login Form Card */}
          <div className="bg-white/95 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-[#1E293B] mb-2">
                {t('loginSignInToAccount')}
              </h2>
              <p className="text-sm text-gray-600">
                {t('loginDontHaveAccount')}{' '}
                <Link
                  to="/register"
                  className="font-semibold text-primary-600 hover:text-primary-700 hover:underline transition-colors"
                >
                  {t('loginSignUpForFree')}
                </Link>
              </p>
            </div>

            <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="phoneNumber"
                control={control}
                render={({ field }) => (
                  <PhoneNumberInput
                    label={t('loginPhoneNumber')}
                    value={field.value || ''}
                    onChange={field.onChange}
                    error={errors.phoneNumber?.message}
                    required
                    defaultCountryCode=""
                  />
                )}
              />

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="password" className="block text-sm font-semibold text-[#1E293B]">
                      {t('loginPassword')}
                    </label>
                    <Link
                      to="/forgot-password"
                      className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
                    >
                      {t('loginForgotPassword')}
                    </Link>
                  </div>
                  <input
                    {...register('password')}
                    type="password"
                    placeholder={t('loginPasswordPlaceholder')}
                    className={`input w-full bg-white border-gray-300 focus:bg-white focus:border-[#1E293B] focus:ring-[#1E293B] ${errors.password ? 'input-error border-danger-300' : ''}`}
                  />
                  {errors.password && (
                    <p className="mt-2 text-sm text-danger-600">{errors.password.message}</p>
                  )}
                </div>

                {/* Remember Me */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="rememberMe"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
                  />
                  <label htmlFor="rememberMe" className={`${isRTL ? 'mr-2' : 'ml-2'} text-sm text-gray-700 cursor-pointer`}>
                    {t('loginRememberMe')}
                  </label>
                </div>

              <div className="pt-3">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3.5 px-6 bg-gradient-to-r from-primary-600 to-primary-700 text-white text-sm font-semibold rounded-xl shadow-lg hover:shadow-xl hover:from-primary-700 hover:to-primary-800 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="loading-spinner w-5 h-5 border-white"></span>
                      {t('loginSigningIn')}
                    </span>
                  ) : (
                    t('loginSignIn')
                  )}
                </button>
              </div>
            </form>

            {/* Quick Login Toggle */}
            <div className="pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowQuickLogin(!showQuickLogin)}
                className="w-full flex items-center justify-between text-sm font-medium text-[#1E293B] hover:text-primary-600 transition-colors py-2"
              >
                <span className="flex items-center gap-2">
                  <span>🚀</span>
                  {t('loginQuickTestLogin')}
                </span>
                <svg
                  className={`w-5 h-5 transition-transform ${showQuickLogin ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Quick Login Content */}
              {showQuickLogin && (
                <div className="mt-4 space-y-4 animate-slide-down">
                  {/* Business Owners */}
                  <div>
                    <h4 className="text-xs font-semibold text-[#1E293B] uppercase tracking-wide mb-3">
                      {t('loginBusinessOwners')}
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      {TEST_USERS.business_owners.map((user, index) => (
                        <button
                          key={index}
                          onClick={() => handleQuickLogin(UserRole.BUSINESS_OWNER, user.phone)}
                          disabled={isLoading}
                          className="text-left px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg hover:bg-[#1E293B]/5 hover:border-[#1E293B]/30 transition-all text-xs disabled:opacity-50 disabled:cursor-not-allowed group"
                        >
                          <div className="font-medium text-[#1E293B] group-hover:text-[#1E293B] truncate">{user.name}</div>
                          <div className="text-xs text-gray-500 group-hover:text-[#1E293B]/70 truncate">{user.phone}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Admins */}
                  <div>
                    <h4 className="text-xs font-semibold text-[#1E293B] uppercase tracking-wide mb-3">
                      {t('loginAdministrators')}
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      {TEST_USERS.admins.map((user, index) => (
                        <button
                          key={index}
                          onClick={() => handleQuickLogin(UserRole.SYSTEM_ADMIN, user.phone)}
                          disabled={isLoading}
                          className="text-left px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg hover:bg-[#1E293B]/5 hover:border-[#1E293B]/30 transition-all text-xs disabled:opacity-50 disabled:cursor-not-allowed group"
                        >
                          <div className="font-medium text-[#1E293B] group-hover:text-[#1E293B] truncate">{user.name}</div>
                          <div className="text-xs text-gray-500 group-hover:text-[#1E293B]/70 truncate">{user.phone}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer Note */}
          <p className="text-center text-xs text-white/70 mt-6">
            {t('loginTermsAgreement')}
          </p>
        </div>
      </div>

      {/* Footer */}
      <Footer variant="compact" />
    </div>
  )
}